import React from "react";
import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { resilienceHeadline, resilienceOverrideReason } from "@/lib/calculations";
import { cn } from "@/lib/utils";

// Single source of truth for the score number, status band, headline
// explanation and safety-override note. Used on FreeResult, Dashboard and
// Resilience so the three screens can never drift apart.
// variant: "card" (light full card) | "tiles" (two tiles) | "hero" (dark row)
export function ResilienceScore({ metrics, variant = "card", className }) {
  const overrideReason = resilienceOverrideReason(metrics);
  const headline = resilienceHeadline(metrics);

  const OverrideBox = ({ dark }) =>
    overrideReason ? (
      <div
        className={cn(
          "mt-3 flex items-start gap-2 rounded-2xl px-4 py-3",
          dark ? "bg-rose-500/15" : "border border-rose-200 bg-rose-50"
        )}
        data-testid="override-reason"
      >
        <AlertCircle className={cn("mt-0.5 h-4 w-4 shrink-0", dark ? "text-rose-300" : "text-rose-500")} />
        <p className={cn("text-xs leading-relaxed", dark ? "text-rose-200" : "text-rose-700")}>
          {overrideReason}
        </p>
      </div>
    ) : null;

  if (variant === "tiles") {
    return (
      <div className={className}>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow">
            <p className="text-xs text-zinc-500">Skor Ketahanan</p>
            <p className="mt-1 font-mono text-3xl font-black text-zinc-900" data-testid="dashboard-score">
              {metrics.score}
            </p>
            <p className="text-xs text-zinc-500">/ 100</p>
          </div>
          <div className="rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow">
            <p className="text-xs text-zinc-500">Status</p>
            <div className="mt-2">
              <StatusBadge status={metrics.status} />
            </div>
          </div>
        </div>
        <OverrideBox dark={false} />
      </div>
    );
  }

  if (variant === "hero") {
    return (
      <>
        <div className="mt-4 flex items-center gap-3 border-t border-white/10 pt-4">
          <StatusBadge status={metrics.status} />
          <span className="font-mono text-sm text-zinc-400" data-testid="resilience-score">
            Skor {metrics.score}/100
          </span>
        </div>
        <OverrideBox dark />
      </>
    );
  }

  // variant === "card"
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn("rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow", className)}
      data-testid="resilience-score-card"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 font-heading">
          Skor Ketahanan
        </p>
        <StatusBadge status={metrics.status} />
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-mono text-5xl font-black text-zinc-900" data-testid="resilience-score">
          {metrics.score}
        </span>
        <span className="font-mono text-xl text-zinc-400">/ 100</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-zinc-500">{headline}</p>
      <OverrideBox dark={false} />
      <p className="mt-2 text-[11px] text-zinc-400">
        Indikator sederhana ketahanan cashflow keluarga.
      </p>
    </motion.div>
  );
}
